import type { ReactElement } from 'react';
import { Link } from 'react-router';

import { avatarColorIndex, nameInitials } from '@/components/ui/avatar';
import { CardFrame, QueryBody } from '@/components/ui/card-frame';
import { cn } from '@/components/ui/cn';
import { type BackState, useBackState } from '@/lib/back-nav';

import { countLabel, deadlineBadge, dueText, responsibleLabel } from '../presentation';
import type { DeadlineItem } from '../types';
import { DEADLINE_WINDOW, useDashboardSummary, useDeadlines } from '../use-dashboard';

// Vencimientos y escalamientos (so=430, spec §3.2; mockup 01): etapas y revisiones que
// vencen en la ventana o ya vencieron, con responsable, estado y a quién se escaló. El
// total real va en el encabezado (nunca se oculta lo que no cabe).

const SUFFIX = `vencidos y próximos ${DEADLINE_WINDOW} días`;

function Responsible({ item }: { item: DeadlineItem }): ReactElement {
  const r = responsibleLabel(item);
  if (r.person === null) {
    return <span className="dim-mono">{r.note}</span>;
  }
  return (
    <div className="who">
      <span className={cn('av sm', `p${avatarColorIndex(r.person.id)}`)} aria-hidden="true">
        {nameInitials(r.person.name)}
      </span>
      <span className="nm">{r.person.name}</span>
      {r.extra > 0 && <span className="dim-mono">+{r.extra}</span>}
      {r.note !== null && <span className="dim-mono">{r.note}</span>}
    </div>
  );
}

function Row({ item, from }: { item: DeadlineItem; from: BackState }): ReactElement {
  const badge = deadlineBadge(item);
  return (
    <tr className={cn(item.overdue && 'alert')}>
      <td>
        <Link to={`/documentos/${item.entry.id}`} state={from} className="code">
          {item.entry.code}
        </Link>
        <div className="dash-sub">{item.entry.title ?? 'Sin título'}</div>
      </td>
      <td>
        <Responsible item={item} />
      </td>
      <td>
        <span className={cn('badge', badge.cls)}>
          <span className="dotb" />
          {badge.label}
        </span>
      </td>
      <td className={cn('dash-due', item.overdue && 'late')}>
        {dueText(item)}
        {item.overdue && item.escalatedTo.length > 0 && (
          <div className="dim-mono dash-late">
            → {item.escalatedTo.map((p) => p.name).join(', ')}
          </div>
        )}
      </td>
    </tr>
  );
}

export function DeadlinesCard({ companyId }: { companyId: string }): ReactElement {
  const query = useDeadlines({ companyId, enabled: true });
  const reviewOverdue = useDashboardSummary({ companyId, enabled: true }).data?.overdue
    .reviewOverdue;
  const d = query.data;
  const fromHere = useBackState('Panel del QMS');
  return (
    <CardFrame
      title="Vencimientos y escalamientos"
      count={d ? countLabel(d.items.length, d.total, SUFFIX) : SUFFIX}
    >
      <QueryBody
        query={query}
        rows={4}
        isEmpty={(data) => data.items.length === 0}
        emptyText={`Sin vencidos ni vencimientos en los próximos ${DEADLINE_WINDOW} días.`}
      >
        {(data) => (
          <div className="t-scroll">
            <table className="t">
              <caption className="sr-only">
                Vencimientos y escalamientos de la empresa activa
              </caption>
              <thead>
                <tr>
                  <th scope="col">Documento</th>
                  <th scope="col">Responsable</th>
                  <th scope="col">Estado</th>
                  <th scope="col">Vence</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <Row
                    key={`${item.kind}-${item.entry.id}-${item.stage ?? ''}`}
                    item={item}
                    from={fromHere}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryBody>
      {reviewOverdue !== undefined && reviewOverdue > 0 && (
        <div className="cb dash-foot">
          <Link to="/documentos?estado=vencidos" className="btn ghost sm">
            Ver revisiones vencidas en la Lista Maestra
          </Link>
        </div>
      )}
    </CardFrame>
  );
}
